import React from "react";
import POC from "../../poc/POC";
import POC2 from "../../poc/POC2";
import Login from "../../sections/login/Login";
import ClientError from "../../sections/client_error/ClientError";
import Home from "../../sections/home/Home";
import Default from "../../sections/home/subroutes/Default";
import SecureRoute from "./SecureRoute";
import PublicRoute from "./PublicRoute";
import SetPassword from "../../sections/login/SetPassword";
import ChangePassword from "../../sections/login/ChangePassword";
import SSOLogout from "../../sections/login/SSOLogout";
import SubmitSuccessPage from "../../sections/record_incident/SubmitSuccessPage";

const ViewIncident = React.lazy(() => import('../../sections/home/subroutes/ViewIncident'));
const RecordIncident = React.lazy(() => import('../../sections/record_incident/RecordIncident'));
const Inspections  = React.lazy(() => import("../../sections/home/subroutes/Inspections"));
const Walkaround  = React.lazy(() => import("../../sections/home/subroutes/Walkaround"));
const VehicleDetails  = React.lazy(() => import("../../sections/home/subroutes/VehicleDetails"));
const ContactDetails  = React.lazy(() => import("../../sections/contact_details/ContactDetails"));
const Information  = React.lazy(() => import("../layout/Information"));

// -------------------------------------------------------------------------------------------------------------------------
// NOTES on PublicRoute or SecureRoute surrounding the element/page option below
// - use SecureRoute if the page is secure i.e. is only reached after login or the user must be logged in to access it
//   (typing in or linking to the URL directly will redirect to login)
// - use PublicRoute if the page does not require the user to be logged in
//
// NOTES on extra attributes
// - vehicleHeader:  set to be 'on'/'off'/'readOnly' (default if not set is 'off') - ONLY FOR PAGES UNDER HOME
// - vehicleHeaderTrans: (optional) if vehicleHeader 'on' or 'readOnly', this is the translation key of a custom title if set
// - tabBarId: set if a mobile tab bar (icons at bottom of smaller screens) should be highlighted
// - backRouteId: set to another route id in here if a back link to that page is to be shown on smaller screens (top left)
// - backRouteTrans: include if backRouteId is set, this is the translation key of the back button text
// -------------------------------------------------------------------------------------------------------------------------

export const driverRoutes = [
    {
        id: "missing",
        path: "*",
        element: <PublicRoute><ClientError notFound={true}/></PublicRoute>
    },
    {
        id: "not-found",
        path: "/404",
        element: <PublicRoute><ClientError notFound={true}/></PublicRoute>
    },
    {
        id: "client-error",
        path: "/error",
        element: <PublicRoute><ClientError/></PublicRoute>
    },
    {
        id: "sso-no-user",
        path: "/sso/no-user",
        element: <PublicRoute><ClientError sso={true} notFound={true}/></PublicRoute>
    },
    {
        id: "sso-error",
        path: "/sso/error",
        element: <PublicRoute><ClientError sso={true}/></PublicRoute>
    },
    {
        id: "start",
        path: "/",
        element: <PublicRoute><Login/></PublicRoute>
    },
    {
        id: "start-sso",
        path: "/external",
        element: <PublicRoute><Login/></PublicRoute>
    },
    {
        id: "finish-sso",
        path: "/logout",
        element: <PublicRoute><SSOLogout/></PublicRoute>
    },
    {
        id: "set-pwd",
        path: "/set-password",
        element: <PublicRoute><SetPassword/></PublicRoute>
    },
    {
        id: "change-pwd",
        path: "/change-password",
        element: <SecureRoute><ChangePassword/></SecureRoute>
    },
    {
        id: "poc1",
        path: "/poc1",
        element: <SecureRoute><POC/></SecureRoute>
    },
    {
        id: "poc2",
        path: "/poc2",
        element: <SecureRoute><POC2/></SecureRoute>
    },
       //Note that main pages should fall under home for notifications to work otherwise code notifications retrieval in that page
       //SecureRoute or PublicRoute not required for pages under Home, all are Secure (from Home)
       //See notes above on extra attributes
    {
        id: "home-base",
        element: <SecureRoute><Home/></SecureRoute>,
        children: [
            {
                id: "home",
                path: "/home",
                element: <Default />,
                vehicleHeader: 'on',
                tabBarId: 'home',
            },
            {
                id: "info",
                path: "/info",
                element: <Information/>,
                vehicleHeader: 'off',
                tabBarId: 'home',
                backRouteId: 'home',
                backRouteTrans: 'title.home',
            },
            {
                id: 'walkaround',
                path: "/walkaround",
                element: <Walkaround />,
                vehicleHeader: 'readOnly',
                tabBarId: 'inspect',
            },
            {
                id: 'inspections',
                path: "/inspections",
                element: <Inspections />,
                vehicleHeader: 'on',
                tabBarId: 'inspect',
            },
            {
                id: 'contact-details',
                path: "/my-details",
                element: <ContactDetails/>,
                vehicleHeader: 'off',
                tabBarId: 'home',
                backRouteId: 'home',
                backRouteTrans: 'title.home',
            },
            {
                id: 'vehicle-details',
                path: "/vehicle-details",
                element: <VehicleDetails/>,
                vehicleHeader: 'on',
                tabBarId: 'home',
                backRouteId: 'home',
                backRouteTrans: 'title.home',
            },
            {
                id: "record-incident",
                path: "/record-incident",
                element: <RecordIncident/>,
                vehicleHeader: 'readOnly',
                vehicleHeaderTrans: 'title.report_incident_for',
                tabBarId: 'incident',
            },
            {
                id: "incident-confirmation",
                path: "/incident-confirmation/:incidentId",
                element: <SubmitSuccessPage/>,
                vehicleHeader: 'readOnly',
                tabBarId: 'incident',
            },
            {
                id: "incident-details",
                path: "/incident-details/:id",
                element: <ViewIncident/>,
                vehicleHeader: 'readOnly',
                tabBarId: 'home',
                backRouteId: 'home',
                backRouteTrans: 'title.home',
            },
          ],
    },
];

export const getDriverRouteById = (id) => {
    const route = driverRoutes.findLast(rte => rte.id === id);
    if (route) {
        return route.path;
    }
    else {
        let childPath = '/404';
        for (let driverRoute of driverRoutes) {
            if (childPath === '/404' && driverRoute.hasOwnProperty('children')) {
                const route = driverRoute.children.findLast(rte => rte.id === id);
                if (route) {
                    childPath = route.path;
                }
            }
        }
        return childPath;
    }
};

export const getDriverRouteObjectFromPath = (path) => {
    function pathCompare(routePath, actualPath)
    {
        if(!routePath && !actualPath)
        {
            return false;
        }
        if(!routePath && actualPath)
        {
            return false;
        }
        if(routePath && !actualPath)
        {
            return false;
        }
        let routeComponents = routePath.split("/");
        let actualComponents = actualPath.split("/");
        if(routeComponents.length !== actualComponents.length)
        { 
            return false;
        }
        for(let step = 0; step < routeComponents.length; step++)
        {
            if(!routeComponents[step].startsWith(":"))
            {
                if(routeComponents[step] !== actualComponents[step])
                {
                    return false;
                }
            }
        }
        return true;
    }
    const route = driverRoutes.findLast(rte => pathCompare(rte.path, path));
    if (route) {
        return route ? route : null;
    }
    else {
        let routeObj = null;
        for (let driverRoute of driverRoutes) {
            if (routeObj == null && driverRoute.hasOwnProperty('children')) {
                const route = driverRoute.children.findLast(rte => pathCompare(rte.path, path));
                if (route) {
                    routeObj = route ? route : null;
                }
            }
        }
        return routeObj;
    }
};

